import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  NormalizedCacheObject,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { removeTypenameFromVariables } from "@apollo/client/link/remove-typename";
import { createCmpInMemoryCache } from "./apolloCache";
import { fetchAuthSession } from "aws-amplify/auth";
import { setContext } from "@apollo/client/link/context";
import { jwtDecode } from "jwt-decode";
import { RoutePaths } from "./enumStrings";
import type { SignOutReason } from "./components/pages/Auth/signOutReasons";
import * as Sentry from "@sentry/react";
import {
  createPageUnloadTracker,
  isNetworkErrorFromPageUnload,
  shouldLogoutForGraphQLErrors,
  shouldReportApolloError,
} from "./utils/apolloErrorPolicy";
import {
  connectionStatus,
  isConnectionFailureError,
} from "./utils/connectionStatus";

const uri =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_PROD_API_URL
    : process.env.REACT_APP_DEV_API_URL;

// Query results carry __typename. GraphQL input types reject it, so strip it
// from variables when a form sends back data it loaded.
export const removeTypenameLink = removeTypenameFromVariables();

// Capability-link pages must not attach a staff session or record their token in telemetry.
export const createPublicApolloClient = () => new ApolloClient({
  link: ApolloLink.from([
    removeTypenameLink,
    new HttpLink({ uri, credentials: "omit", fetchOptions: { referrerPolicy: "no-referrer" } }),
  ]),
  cache: createCmpInMemoryCache(),
});

const pageUnloadTracker = createPageUnloadTracker(window);

const getNewAccessToken = async (): Promise<string | undefined> => {
  try {
    const session = await fetchAuthSession(); // add method to get token here

    if (session && session.tokens && session.tokens.accessToken) {
      return session.tokens.accessToken.toString();
    }

    return undefined;
  } catch (e) {
    console.log("Error getting access token for apollo client:", e);
  }

  return undefined;
};

const getApolloClient = async (
  handleLogout: (reason?: SignOutReason) => void
): Promise<ApolloClient<NormalizedCacheObject>> => {
  const errorLink = onError((e) => {
    const urlPath = window.location.pathname;
    const operationContext = e.operation.getContext();
    const preserveErrorForCaller =
      operationContext.preserveErrorForCaller === true;
    // A request that got no HTTP answer may mean the internet is down.
    const isConnectionFailure = isConnectionFailureError(e.networkError);
    const reportApolloError = shouldReportApolloError({
      context: operationContext,
      graphQLErrors: e.graphQLErrors,
      hasNetworkError: Boolean(e.networkError),
    });

    // Expected caller-preserved business errors stay on the current screen.
    // Unexpected failures remain observable without serializing potentially
    // sensitive operation variables to the console or Sentry.
    if (reportApolloError) {
      if (!preserveErrorForCaller) {
        console.log("Caught Apollo Client Error", e);
      }

      Sentry.withScope((scope) => {
        const { operation, graphQLErrors, networkError } = e;
        scope.setTransactionName(operation.operationName);
        scope.setContext(
          "apolloGraphQLOperation",
          preserveErrorForCaller
            ? {
                operationName: operation.operationName,
                callerHandled: true,
              }
            : {
                operationName: operation.operationName,
                variables: operation.variables,
                extensions: operation.extensions,
              }
        );

        graphQLErrors?.forEach((error) => {
          Sentry.captureMessage(error.message, {
            level: "error",
            fingerprint: ["{{ default }}", "{{ transaction }}"],
            contexts: {
              apolloGraphQLError: {
                error,
                message: error.message,
                extensions: error.extensions,
              },
            },
          });
        });

        // A request cut off by a reload, close, or full navigation is not an
        // API failure. The /500 redirect below is unchanged.
        // A known offline browser is not an API failure either.
        if (
          networkError &&
          !(isConnectionFailure && connectionStatus.isOffline()) &&
          !isNetworkErrorFromPageUnload({
            networkError,
            pageIsUnloading: pageUnloadTracker.isPageUnloading(),
          })
        ) {
          Sentry.captureMessage(networkError.message, {
            level: "error",
            contexts: {
              apolloNetworkError: {
                error: networkError,
                extensions: (networkError as any).extensions,
              },
            },
          });
        }
      });
    }

    if (shouldLogoutForGraphQLErrors(e.graphQLErrors)) {
      if (urlPath !== RoutePaths.logout) {
        handleLogout("sessionEnded");
      }
      return;
    }

    const redirectToError500 = () => {
      if (!preserveErrorForCaller && urlPath !== RoutePaths.error500) {
        window.location.href = RoutePaths.error500;
      }
    };

    // Offline design A: with no internet, stay on the page so typed changes
    // are not lost, and the offline banner shows. A /500 reload would fail
    // offline and lose the page. A server that is down while the internet
    // works still goes to /500 as before.
    if (isConnectionFailure) {
      void connectionStatus.checkConnection().then((isOnline) => {
        if (isOnline) {
          redirectToError500();
        }
      });
      return;
    }

    redirectToError500();
  });

  let accessToken = await getNewAccessToken();

  const authLink = setContext(async (operation, previousContext) => {
    // refresh token if it's expired bruv and not a guest
    if (
      accessToken &&
      Date.now() >=
        (jwtDecode(accessToken as any as string).exp as number) * 1000
    ) {
      accessToken = await getNewAccessToken();
    }

    let apiAuthorization = accessToken ? `Bearer ${accessToken}` : "";

    // attach access token for the manual requests made during setting a permanent password while using a temporary password
    if (
      previousContext &&
      previousContext?.customAccessTokenForSettingHasSignedUp &&
      previousContext?.customAccessTokenForSettingHasSignedUp?.length > 0
    ) {
      apiAuthorization = `Bearer ${previousContext.customAccessTokenForSettingHasSignedUp}`;
    }

    if (
      previousContext &&
      previousContext?.customAccessTokenToCheckIfLoggingInUserHasAccessToChamberMemberPro &&
      previousContext
        ?.customAccessTokenToCheckIfLoggingInUserHasAccessToChamberMemberPro
        ?.length > 0
    ) {
      apiAuthorization = `Bearer ${previousContext.customAccessTokenToCheckIfLoggingInUserHasAccessToChamberMemberPro}`;
    }

    return {
      ...previousContext, // ensure previous context is passed along to sustain cache and all the other apollo graphql goodies (without passing this everything breaks)
      headers: {
        authorization: apiAuthorization,
      },
    };
  });

  const httpLink = new HttpLink({
    uri,
  });

  const link = ApolloLink.from([
    removeTypenameLink,
    errorLink,
    authLink,
    httpLink,
  ]);

  const client = new ApolloClient({
    link,
    cache: createCmpInMemoryCache(),
  });

  return client;
};

export default getApolloClient;
